const normalizeHost = host => {
  const trimmedHost = (host || '').trim();
  if (!trimmedHost) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmedHost)) {
    return trimmedHost.replace(/\/+$/, '');
  }

  return `https://${trimmedHost.replace(/^\/+/, '').replace(/\/+$/, '')}`;
};

const waitForMktoForms = timeoutMs =>
  new Promise((resolve, reject) => {
    if (window.MktoForms2) {
      resolve(window.MktoForms2);
      return;
    }

    const start = Date.now();
    const poll = window.setInterval(() => {
      if (window.MktoForms2) {
        window.clearInterval(poll);
        resolve(window.MktoForms2);
        return;
      }

      if (Date.now() - start >= timeoutMs) {
        window.clearInterval(poll);
        reject(new Error('MktoForms2 not available (expected from GTM).'));
      }
    }, 50);
  });

const loadMarketoScriptFallback = host =>
  new Promise((resolve, reject) => {
    const normalizedHost = normalizeHost(host);
    if (!normalizedHost) {
      reject(new Error('Marketo host is required for fallback loading.'));
      return;
    }

    if (window.MktoForms2) {
      resolve(window.MktoForms2);
      return;
    }

    const existingScript = document.querySelector('script[src*="/js/forms2/js/forms2.min.js"]');
    if (existingScript) {
      // Script tag exists (possibly from GTM); wait for global to initialize.
      waitForMktoForms(1500).then(resolve).catch(reject);
      return;
    }

    const script = document.createElement('script');
    script.src = `${normalizedHost}/js/forms2/js/forms2.min.js`;
    script.async = true;
    script.onload = () => {
      waitForMktoForms(1500).then(resolve).catch(reject);
    };
    script.onerror = () => reject(new Error('Failed to load fallback Marketo forms script.'));
    document.head.appendChild(script);
  });

const setStatus = (statusNode, type, message) => {
  if (!statusNode) {
    return;
  }

  statusNode.innerHTML = message;
  statusNode.classList.remove('is-success', 'is-error');
  statusNode.classList.add('is-visible');
  statusNode.classList.add(type === 'success' ? 'is-success' : 'is-error');
};

const decorateRequiredConsentCheckbox = form => {
  const formElem = typeof form.getFormElem === 'function' ? form.getFormElem() : null;
  const rootNode = formElem && formElem.length ? formElem[0] : null;
  if (!rootNode) {
    return;
  }

  const targetLabel = Array.from(rootNode.querySelectorAll('.mktoCheckboxList > label')).find(
    labelNode => {
      const text = (labelNode.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      return text.includes('trial software license') && text.includes('terms and conditions');
    }
  );

  if (!targetLabel || targetLabel.querySelector('.mktoAsterix')) {
    return;
  }

  const fieldWrap = targetLabel.closest('.mktoFieldWrap');
  if (!fieldWrap || !fieldWrap.classList.contains('mktoRequiredField')) {
    return;
  }

  const asterisk = document.createElement('span');
  asterisk.className = 'mktoAsterix mktoAsterix--consent';
  asterisk.setAttribute('aria-hidden', 'true');
  asterisk.textContent = '*';
  targetLabel.insertBefore(asterisk, targetLabel.firstChild);
};

const dispatchMarketoEvent = (wrapper, name, detail = {}) => {
  const payload = { ...detail, source: 'sag/ajax-form' };
  // Bubbles through document to window, so window listeners hear it once.
  wrapper.dispatchEvent(new CustomEvent(name, { detail: payload, bubbles: true }));
};

const initializeForm = (wrapper, MktoForms2) => {
  if (wrapper.dataset.marketoAjaxInitialized === '1') {
    return;
  }

  const formId = Number.parseInt(wrapper.dataset.marketoFormId || '', 10);
  const marketoHost = normalizeHost(wrapper.dataset.marketoHost || '');
  const munchkinId = (wrapper.dataset.marketoMunchkinId || '').trim();
  const successMessageTemplate = wrapper.querySelector('[data-marketo-success-template]');
  const errorMessageTemplate = wrapper.querySelector('[data-marketo-error-template]');
  const successMessage =
    (successMessageTemplate && successMessageTemplate.innerHTML.trim()) ||
    '<p><strong>Thanks.</strong> Your form was submitted successfully.</p>';
  const errorMessage =
    (errorMessageTemplate && errorMessageTemplate.innerHTML.trim()) ||
    '<p>Please review the highlighted fields and try again.</p>';
  const statusNode = wrapper.querySelector('[data-marketo-ajax-status]');
  const shellNode = wrapper.querySelector('[data-marketo-ajax-shell]');
  const formContainer = wrapper.querySelector('.o-ajax-form__form-container');

  if (!formId || !marketoHost || !munchkinId) {
    setStatus(statusNode, 'error', 'Form configuration is incomplete.');
    return;
  }

  wrapper.dataset.marketoAjaxInitialized = '1';

  MktoForms2.loadForm(marketoHost, munchkinId, formId, form => {
    const normalizeFormPresentation = () => {
      const formElem = typeof form.getFormElem === 'function' ? form.getFormElem() : null;
      const rootNode = formElem && formElem.length ? formElem[0] : null;
      if (!rootNode) {
        return;
      }

      rootNode.querySelectorAll('.mktoLabel').forEach(labelNode => {
        labelNode.style.removeProperty('width');
      });

      rootNode.querySelectorAll('.mktoButtonWrap').forEach(buttonWrapNode => {
        buttonWrapNode.style.removeProperty('margin-left');
      });
    };

    normalizeFormPresentation();
    decorateRequiredConsentCheckbox(form);

    // The Marketo fields are now in the DOM; drop the reserved-space skeleton.
    if (formContainer) {
      formContainer.classList.add('is-form-loaded');
    }

    // Force no-redirect behavior even if other scripts register additional onSuccess callbacks later.
    if (!form.__sagNoRedirectPatched && typeof form.onSuccess === 'function') {
      const originalOnSuccess = form.onSuccess.bind(form);
      form.onSuccess = callback =>
        originalOnSuccess((values, followUpUrl) => {
          try {
            if (typeof callback === 'function') {
              callback(values, followUpUrl);
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Marketo onSuccess callback error.', error);
          }

          return false;
        });
      form.__sagNoRedirectPatched = true;
    }

    form.onValidate(() => {
      normalizeFormPresentation();
      decorateRequiredConsentCheckbox(form);
      if (!form.submittable()) {
        setStatus(statusNode, 'error', errorMessage);
        dispatchMarketoEvent(wrapper, 'marketoForm:error', { formId, reason: 'validation' });
      }
    });

    form.onSuccess((values, followUpUrl) => {
      try {
        if (shellNode && formContainer) {
          const currentHeight = formContainer.offsetHeight || shellNode.offsetHeight || 0;
          if (currentHeight > 0) {
            shellNode.style.minHeight = `${currentHeight}px`;
          }
          shellNode.classList.add('is-submitted');
        }

        if (formContainer) {
          formContainer.classList.add('is-submitted');
        }

        setStatus(statusNode, 'success', successMessage);
        dispatchMarketoEvent(wrapper, 'marketoForm:success', { formId, followUpUrl, values });
      } catch (error) {
        // Keep users on-page even if custom success UI/event code fails.
        // eslint-disable-next-line no-console
        console.error('Marketo AJAX form success handler failed.', error);
      }

      return false;
    });

    // Safety net: always suppress Marketo follow-up redirect for this block.
    form.onSuccess(() => false);
  });
};

const MarketoAjaxForms = () => {
  const forms = Array.from(document.querySelectorAll('[data-marketo-ajax-form]'));
  if (!forms.length) {
    return;
  }

  const fallbackHost = forms[0].dataset.marketoHost || '';
  const existingScript = document.querySelector('script[src*="/js/forms2/js/forms2.min.js"]');
  const marketoReadyPromise = window.MktoForms2
    ? Promise.resolve(window.MktoForms2)
    : existingScript
      ? waitForMktoForms(1500).catch(() => loadMarketoScriptFallback(fallbackHost))
      : loadMarketoScriptFallback(fallbackHost);

  marketoReadyPromise
    .then(MktoForms2 => {
      forms.forEach(wrapper => initializeForm(wrapper, MktoForms2));
    })
    .catch(() => {
      forms.forEach(wrapper => {
        const statusNode = wrapper.querySelector('[data-marketo-ajax-status]');
        const formContainer = wrapper.querySelector('.o-ajax-form__form-container');
        if (formContainer) {
          // Stop the loading skeleton even if the form never arrived.
          formContainer.classList.add('is-form-loaded');
        }
        setStatus(statusNode, 'error', '<p>We could not load this form right now.</p>');
      });
    });
};

export default MarketoAjaxForms;
